import { withNamespaces } from "react-i18next";
import { Offcanvas } from "react-bootstrap";
import { Button } from "reactstrap";

const translate = (t, copy, literal = false) => {
    if (literal && (typeof copy === "string" || copy == null)) {
        return copy || "";
    }

    if (copy && typeof copy === "object") {
        if (copy.literal != null) {
            return copy.literal;
        }

        if (Array.isArray(copy.labels)) {
            return copy.labels.map((label) => t(label)).join(", ");
        }

        return t(copy.key, copy.values);
    }

    if (!copy) {
        return "";
    }

    return t(copy);
};

const Badge = ({ t, label, tone, icon }) => {
    if (!label) {
        return null;
    }

    return (
        <span className={`assessment-summary-offcanvas__badge ${tone || ""}`}>
            {icon && <i className={icon} aria-hidden="true" />}
            {t(label)}
        </span>
    );
};

const TableLayout = ({ t, columns = [], items = [] }) => {
    return (
        <div className="assessment-summary-offcanvas__table-wrap">
            <table className="assessment-summary-offcanvas__table">
                <thead>
                    <tr>
                        <th>#</th>
                        {columns.map((column) => (
                            <th key={column.key}>{t(column.label)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={item.id || index}>
                            <td>{index + 1}</td>
                            {columns.map((column) => {
                                const cell = item[column.key];

                                if (column.key === "title") {
                                    return (
                                        <td key={column.key}>
                                            <strong>{t(item.title)}</strong>
                                            {item.description && (
                                                <small>{t(item.description)}</small>
                                            )}
                                        </td>
                                    );
                                }

                                if (cell && typeof cell === "object") {
                                    return (
                                        <td key={column.key}>
                                            <Badge
                                                t={t}
                                                label={cell.label}
                                                tone={cell.tone}
                                            />
                                        </td>
                                    );
                                }

                                return (
                                    <td key={column.key}>
                                        {cell ? t(String(cell)) : "—"}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ListLayout = ({ t, items = [] }) => {
    return (
        <ol className="assessment-summary-offcanvas__list">
            {items.map((item, index) => (
                <li key={item.id || index}>
                    <span className="assessment-summary-offcanvas__index">
                        {index + 1}
                    </span>
                    <strong>{t(item.title)}</strong>
                    <Badge t={t} label={item.badge} tone={item.badgeTone} />
                </li>
            ))}
        </ol>
    );
};

const SectionsLayout = ({ t, sections = [] }) => {
    return (
        <div className="assessment-summary-offcanvas__sections">
            {sections.map((section) => (
                <section key={section.title}>
                    <h6 className="assessment-summary-offcanvas__section-title">
                        {t(section.title)}
                        {section.count != null && (
                            <span className="assessment-summary-offcanvas__section-count">
                                {section.count}
                            </span>
                        )}
                    </h6>

                    {section.type === "badges" && (
                        <div className="assessment-summary-offcanvas__chips">
                            {(section.items || []).map((item) => (
                                <Badge
                                    key={item.label}
                                    t={t}
                                    label={item.label}
                                    tone={item.tone}
                                    icon={item.icon}
                                />
                            ))}
                        </div>
                    )}

                    {section.type === "text" && (
                        <p className="assessment-summary-offcanvas__section-text">
                            {translate(t, section.text, section.literal)}
                        </p>
                    )}

                    {section.type === "bullets" && (
                        <ul className="assessment-summary-offcanvas__bullets">
                            {(section.items || []).map((item) => (
                                <li key={item}>
                                    {section.literal ? item : t(item)}
                                </li>
                            ))}
                        </ul>
                    )}

                    {section.type === "warnings" && (
                        <ul className="assessment-summary-offcanvas__warnings">
                            {(section.items || []).map((item) => (
                                <li key={item.key}>
                                    <strong>{t(item.title)}</strong>
                                    {item.body ? <p>{item.body}</p> : null}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            ))}
        </div>
    );
};

const AssessmentSummaryOffcanvas = ({
    t,
    isOpen,
    details,
    onClose
}) => {
    const title = details?.title || "Summary";
    const icon = details?.icon || "ri-file-list-3-line";
    const items = details?.items || [];
    const sections = details?.sections || [];
    const isEmpty =
        (details?.layout === "sections" && !sections.length) ||
        (details?.layout !== "sections" && !items.length);

    return (
        <Offcanvas
            show={isOpen}
            onHide={onClose}
            placement="end"
            className="assessment-summary-offcanvas"
        >
            <Offcanvas.Header>
                <Offcanvas.Title>
                    <span
                        className={`assessment-summary-offcanvas__icon ${
                            details?.iconTone || "blue"
                        }`}
                    >
                        <i className={icon} aria-hidden="true" />
                    </span>
                    <span>{t(title)}</span>
                </Offcanvas.Title>
                <button
                    type="button"
                    className="assessment-summary-offcanvas__close"
                    aria-label={t("Close")}
                    onClick={onClose}
                >
                    <i className="ri-close-line" aria-hidden="true" />
                </button>
            </Offcanvas.Header>

            <Offcanvas.Body>
                {details?.subtitle && (
                    <p className="assessment-summary-offcanvas__subtitle">
                        {translate(t, details.subtitle)}
                    </p>
                )}

                {isEmpty ? (
                    <p className="text-muted mb-0">
                        {t(
                            details?.emptyMessage ||
                                "No details are available for this summary yet."
                        )}
                    </p>
                ) : details?.layout === "table" ? (
                    <TableLayout
                        t={t}
                        columns={details.columns}
                        items={items}
                    />
                ) : details?.layout === "list" ? (
                    <ListLayout t={t} items={items} />
                ) : (
                    <SectionsLayout t={t} sections={sections} />
                )}
            </Offcanvas.Body>

            <div className="assessment-summary-offcanvas__footer">
                <Button color="link" type="button" onClick={onClose}>
                    {t("Close")}
                </Button>
            </div>
        </Offcanvas>
    );
};

export default withNamespaces()(AssessmentSummaryOffcanvas);
